import { LegalPage, LegalSection, LegalSubheading } from "@/components/Legal";
import { BETA_EMAIL } from "@/components/Layout";
import { Brand } from "@/components/Logo";

const EFFECTIVE = "October 15, 2026";

const PROVIDERS = [
  {
    name: "Emergent",
    does: "Hosting, database (MongoDB), photo storage, push notification delivery, and access to the AI models below. Data is stored and processed in the United States.",
  },
  { name: "Twilio", does: "Sending phone verification codes." },
  {
    name: "Postmark",
    does: "Sending emails (verification, sign-in links, password resets, notifications) and receiving emails you forward to your PeopleiKnow address.",
  },
  {
    name: "OpenAI",
    does: "Reading business cards, screenshots and forwarded emails; transcribing voice recordings.",
  },
  { name: "Anthropic", does: "Understanding requests to Ask PeopleiKnow AI." },
  {
    name: "Google",
    does: "Google Sign-In; Gemini AI for enrichment and voice notes; Google Maps and Places for addresses and nearby places.",
  },
  { name: "Apple", does: "Sign in with Apple; iPhone Contacts; push notifications." },
  {
    name: "OpenStreetMap (Nominatim) and Photon (Komoot)",
    does: "Turning locations into addresses and suggesting places.",
  },
  {
    name: "Public web search engines",
    does: "Web searches for enrichment, when you choose it. These receive the search terms (such as a name and city), not your account details.",
  },
];

export default function Privacy() {
  return (
    <LegalPage
      testId="privacy-page"
      title="Privacy Policy"
      subtitle="This is the plain-language version. We'd rather you actually read this than scroll past a wall of legalese."
      effectiveDate={EFFECTIVE}
      dateLabel="Last updated"
    >
      <LegalSection title="Who we are">
        <p>
          <Brand /> ("PeopleiKnow", "we", "us" or "our") is a personal contacts
          and relationship app. It runs as an iPhone app and on the web at{" "}
          <a className="text-blue-700 font-medium hover:underline" href="https://app.peopleiknow.ai">
            app.peopleiknow.ai
          </a>
          . <Brand /> is operated by Foosball Consulting, Inc., Calgary, Alberta,
          Canada.
        </p>
        <p>
          This policy explains what information we collect, how we use it, who
          we share it with, and the choices you have. You can reach us anytime
          at{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="Information we collect">
        <LegalSubheading>Information you give us</LegalSubheading>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="font-medium text-slate-700">Account details.</span>{" "}
            Your name, email address and password (stored only as a secure
            hash), and your phone number if you choose to verify it.
          </li>
          <li>
            <span className="font-medium text-slate-700">Sign-in details.</span>{" "}
            You can sign in with Google, Sign in with Apple, email and
            password, or a one-time sign-in link sent to your email (a "magic
            link"). If you use Google or Apple, we receive your name, email
            address and a unique account identifier from them. For Google, we
            also receive your profile photo if available. Microsoft sign-in is
            not offered in the live product.
          </li>
          <li>
            <span className="font-medium text-slate-700">
              Your own card ("My Card").
            </span>{" "}
            The details you choose to put on your own card, such as your name,
            photo, phone numbers, emails, addresses, city, company, job title,
            school, birthday, LinkedIn, website and social accounts.
          </li>
          <li>
            <span className="font-medium text-slate-700">Your contacts.</span>{" "}
            The people you add and the details you store about them. This
            includes private notes and comments, how-we-met notes, groups,
            reminders, photos, and the display names you give people.
          </li>
          <li>
            <span className="font-medium text-slate-700">Content you capture.</span>{" "}
            If you use them: photos of business cards or screenshots, emails
            you forward to your private <Brand /> forwarding address, and
            voice recordings you make to add a contact or a note.
          </li>
          <li>
            <span className="font-medium text-slate-700">
              Communications with us.
            </span>{" "}
            Emails, feedback and crash reports you choose to send us.
          </li>
        </ul>

        <LegalSubheading>
          Information from your iPhone, with your permission
        </LegalSubheading>
        <p>
          <Brand /> asks for each of these through iOS. You can say no, or
          change your mind later in your iPhone Settings.
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="font-medium text-slate-700">Contacts.</span> If
            you allow it, <Brand /> reads your iPhone contacts so it can
            import them and keep them in sync. The contacts you share with{" "}
            <Brand /> are copied to your <Brand /> account on our servers. If
            you choose Limited Access, that means only the ones you picked.
            While permission is on, the app checks your address book again
            when you open it, so changes you made in the iPhone Contacts app
            can show up in <Brand /> for your review. <Brand /> adds, updates
            or deletes contacts on your iPhone only when you review and
            approve those changes.
          </li>
          <li>
            <span className="font-medium text-slate-700">
              Location (while using the app).
            </span>{" "}
            Used to remember where you met someone, and to suggest nearby
            places and addresses.
          </li>
          <li>
            <span className="font-medium text-slate-700">Camera and photos.</span>{" "}
            Used to scan business cards and add photos to contacts or your own
            card.
          </li>
          <li>
            <span className="font-medium text-slate-700">Microphone.</span>{" "}
            Used when you choose to add a contact or a note by speaking.
          </li>
          <li>
            <span className="font-medium text-slate-700">Notifications.</span>{" "}
            Used to tell you about connection requests, updates from your
            contacts and reminders.
          </li>
        </ul>

        <LegalSubheading>Information collected automatically</LegalSubheading>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="font-medium text-slate-700">
              Device and session information.
            </span>{" "}
            For example, device type, app version, browser, sign-in times and
            the devices where you're signed in. We also collect your IP
            address and approximate location with each sign-in session, for
            security and so you can see and sign out of your active sessions
            in the app.
          </li>
          <li>
            <span className="font-medium text-slate-700">Usage information.</span>{" "}
            We use usage analytics to understand how <Brand /> is used and to
            improve the app. We don't use advertising tools or ad tracking,
            and we don't sell your data.
          </li>
        </ul>
        <p>
          We aim to collect only what's needed to provide and improve the
          features you use.
        </p>
      </LegalSection>

      <LegalSection title="How we use information">
        <p>We use information to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Provide, maintain and improve <Brand />.</li>
          <li>
            Create and secure your account, verify your email and phone
            number, and keep you signed in.
          </li>
          <li>
            Import, organize and sync your contacts, including with your
            iPhone Contacts when you approve changes.
          </li>
          <li>Deliver the sharing and connection features you choose (described below).</li>
          <li>Run the optional AI features you use (described below).</li>
          <li>
            Send you service messages, such as verification codes, sign-in
            links, password resets and notifications.
          </li>
          <li>Respond to your requests, questions, feedback and crash reports.</li>
          <li>
            Protect <Brand />, our users and the integrity of your data,
            including preventing abuse and fraud.
          </li>
          <li>Meet our legal obligations.</li>
        </ul>
        <p>
          We do not sell your personal data, and we do not use it for
          advertising.
        </p>
      </LegalSection>

      <LegalSection title="AI features">
        <p>
          <Brand /> includes optional AI features. They only work on
          information you already have in <Brand />, or that you give them.
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="font-medium text-slate-700">
              Ask <Brand /> AI.
            </span>{" "}
            When you type a request, it's sent to an AI model (currently
            Anthropic's Claude) to work out what you want. <Brand /> then
            matches it against your own contacts and shows you a plan.
            Nothing changes until you confirm.
          </li>
          <li>
            <span className="font-medium text-slate-700">
              Capture from cards, screenshots and emails.
            </span>{" "}
            When you scan a business card, upload a screenshot or forward an
            email, its content is sent to an AI model (currently OpenAI) to
            pull out contact details for you to review.
          </li>
          <li>
            <span className="font-medium text-slate-700">Voice.</span> When
            you record a voice note or speak a new contact's details, the
            recording is transcribed by OpenAI's speech-to-text service. An AI
            model (currently Google Gemini) may draft a short note from the
            transcript.
          </li>
          <li>
            <span className="font-medium text-slate-700">Enrichment.</span>{" "}
            Enrichment runs only when you choose to run it. When you do,{" "}
            <Brand /> runs public web searches using that person's name and
            details you've already saved, such as city, company or school. It
            then asks an AI model (currently Google Gemini) whether the
            results match. High-confidence matches can automatically update a
            contact's saved city, region and country in <Brand />, including
            replacing existing values. Other suggestions need your approval.
            AI location changes are not automatically sent to your iPhone
            Contacts. People you're connected with on <Brand /> are left out
            of enrichment, because their details come from them.
          </li>
        </ul>
        <p>
          These AI providers are reached through our platform provider,
          Emergent. We send only the information needed for the feature you
          use. Our providers process it to deliver that feature.
        </p>
      </LegalSection>

      <LegalSection title="Sharing and your controls">
        <p>
          <Brand /> is built around one idea: you decide what other people get
          from you. Here's how each type of sharing works.
        </p>

        <LegalSubheading>My Card and your QR code</LegalSubheading>
        <p>
          My Card is a snapshot of whatever you choose to show. When someone
          scans your QR code or opens your card, they get exactly what's
          displayed on My Card at that moment, nothing more. They don't get
          future updates unless the two of you connect in <Brand />. You can
          change what My Card shows anytime, for any situation.
        </p>

        <LegalSubheading>Connected contacts</LegalSubheading>
        <p>
          When you and another person connect in <Brand />, you each get the
          details the other has chosen to share with connections. When you
          update those details, your connections receive the updates.
        </p>
        <p>
          Only your first name is always shared. Everything else is your
          choice, including your last name.
        </p>

        <LegalSubheading>Selected contacts</LegalSubheading>
        <p>
          You can choose to share additional details with specific contacts,
          on top of what all your connections get.
        </p>

        <LegalSubheading>What is never shared</LegalSubheading>
        <p>
          These stay private to you, always: your private notes and comments,
          your how-we-met notes, additional photos you add to contacts, and
          the display names you give other people.
        </p>

        <LegalSubheading>Your privacy settings</LegalSubheading>
        <p>
          You control your own settings in Settings &gt; Privacy &amp;
          Permissions &gt; What my connections get, and you can change them
          anytime. Changes apply to what people get from you going forward.
          They don't remove details someone has already saved on their own
          card for you.
        </p>

        <LegalSubheading>Requests for additional details (coming soon)</LegalSubheading>
        <p>
          Someone may be able to ask you for more details. A request is only a
          request. You decide whether to share anything.
        </p>
      </LegalSection>

      <LegalSection title="Finding each other on PeopleiKnow">
        <p>
          <span className="font-medium text-slate-700">Search by name.</span>{" "}
          Other <Brand /> members can find you by your first and last name.
          Search results show only your name, profile photo and <Brand /> ID,
          never your email or phone number.
        </p>
        <p>
          <span className="font-medium text-slate-700">
            Lookup by phone or email.
          </span>{" "}
          When a member adds a contact by phone number or email, <Brand /> may
          show that the person is on <Brand />. This only happens if that
          person has chosen to share that phone number or email. Lookups are
          rate-limited to prevent scraping.
        </p>
      </LegalSection>

      <LegalSection title="When we disclose information for other reasons">
        <p>We may also disclose information:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            To our service providers, as described below, only to run{" "}
            <Brand />.
          </li>
          <li>
            If required by law, or to protect the rights, safety and security
            of our users, the public or <Brand />.
          </li>
          <li>
            As part of a merger, acquisition or sale of all or part of our
            business, in which case this policy, or one at least as
            protective, would continue to apply. We'd tell you before your
            information became subject to a different policy.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Receiving information from your contacts">
        <p>
          When contacts share with you or update their details, <Brand />{" "}
          keeps you in control of your own address book:
        </p>
        <p>
          <span className="font-medium text-slate-700">
            Proposals you decide on.
          </span>{" "}
          Name changes, a new photo when you already have one, and a phone
          number or email that conflicts with one you entered yourself are
          shown as proposals. You choose Keep mine or Use theirs.
        </p>
        <p>
          <span className="font-medium text-slate-700">
            Automatic updates, with a heads-up.
          </span>{" "}
          When a connected contact updates details they share with
          connections, <Brand /> updates your card for them automatically and
          tells you, and you can recover the earlier value from Past Changes.
          This applies to company, job title, work address, home city,
          province or state, and home address, and to a phone number or email
          they have verified. If you don't have a photo for them, theirs
          fills in. Details you typed in yourself are never removed
          automatically.
        </p>
        <p>
          Nothing is overwritten silently. You'll always be able to see what
          changed and who it came from.
        </p>
        <p>
          Updates from contacts don't go to your iPhone Contacts unless you
          approve them.
        </p>
      </LegalSection>

      <LegalSection title="Other people's information">
        <p>Your address book contains information about other people. We take that seriously.</p>
        <p>
          <span className="font-medium text-slate-700">
            Imported contacts are private to you.
          </span>{" "}
          We don't share the contact details you store about other people
          with other <Brand /> members or with any third party for their own
          purposes. We don't use them to build profiles of people, and we
          don't sell them.
        </p>
        <p>
          <span className="font-medium text-slate-700">Service providers.</span>{" "}
          To run the features you use, some of this information is processed
          by the service providers listed below. For example, it is stored by
          our hosting provider, sent to an AI provider when you scan a card,
          or searched on the web when you choose enrichment. They process it
          only on our behalf.
        </p>
        <p>
          <span className="font-medium text-slate-700">
            Finding which of your contacts are on <Brand />.
          </span>{" "}
          When you import contacts, <Brand /> compares their email addresses
          and phone numbers with <Brand /> accounts. If there's a match,
          you'll see that the person is on <Brand />, and they'll get a
          notification that you added them from your phone, asking whether to
          send you their future updates. Likewise, when someone joins{" "}
          <Brand /> and you already have them saved, you'll each be told and
          invited to connect. Neither of you sees the details the other has
          stored. Each person only gets what the other chooses to share, and
          only if you connect.
        </p>
        <p>
          <span className="font-medium text-slate-700">Mutual connections.</span>{" "}
          When you view a contact who is a <Brand /> member, you may see the{" "}
          <Brand /> members you both have in your contacts. You only ever see
          people who are already in your own contacts.
        </p>
        <p>
          <span className="font-medium text-slate-700">Your responsibility.</span>{" "}
          Please only add information about other people that you're allowed
          to have, and use it respectfully.
        </p>
        <p>
          If you're not a <Brand /> member and want to ask about information
          related to you, email us at{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="Google account and Google API data" highlight>
        <p>
          <span className="font-medium text-slate-700">Google Sign-In.</span> If
          you choose "Continue with Google" to create, sign in to or link a{" "}
          <Brand /> account, we receive your basic Google profile information:
          your name, email address, profile photo (if available) and a unique
          Google account identifier. We use this only to authenticate you,
          create or link your <Brand /> account, prevent duplicate accounts,
          keep you signed in, and provide account-related support and
          security.
        </p>
        <p>
          We don't get access to your Gmail, Google Drive, Google Calendar,
          Google Contacts or other Google content just because you use Google
          Sign-In. <Brand /> asks for additional Google permissions only if
          you choose and authorize a separate feature that clearly needs
          them.
        </p>
        <p>
          If you choose to connect Google services, <Brand /> may access{" "}
          <span className="font-medium text-slate-700">
            only the Google data you explicitly authorize
          </span>{" "}
          through Google's consent screen, using the minimum permissions
          needed for the features you've turned on.
        </p>
        <p>
          Google-sourced data is used{" "}
          <span className="font-medium text-slate-700">
            only to provide or improve the user-facing features you ask for
          </span>{" "}
          within the app. We don't sell Google user data, we don't transfer it
          for advertising, and we don't use it for purposes unrelated to the
          features you asked for.
        </p>
        <p className="rounded-xl bg-white border border-blue-200 p-4 text-slate-700">
          <Brand />'s use and transfer of information received from Google
          APIs to any other app will adhere to the{" "}
          <a
            className="text-blue-700 font-medium hover:underline"
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
        <p>
          You can review or remove <Brand />'s access to your Google account
          anytime from your{" "}
          <a
            className="text-blue-700 font-medium hover:underline"
            href="https://myaccount.google.com/permissions"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Account permissions
          </a>{" "}
          page.
        </p>
      </LegalSection>

      <LegalSection title="Sign in with Apple">
        <p>
          If you use Sign in with Apple, we receive your name (the first time
          only), your email address, or a private relay address if you choose
          to hide yours, and a unique Apple identifier. When you delete your{" "}
          <Brand /> account in the iPhone app, we also revoke <Brand />'s Sign
          in with Apple access. If that can't be done automatically, or if you
          delete your account on the web, you can remove <Brand /> yourself on
          your iPhone in Settings &gt; your name &gt; Sign in with Apple.
        </p>
      </LegalSection>

      <LegalSection title="Service providers">
        <p>
          We use a small number of trusted providers to run <Brand />. They
          process information only on our behalf and under appropriate
          confidentiality and data-protection obligations.
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full table-fixed text-sm text-left border-collapse" data-testid="service-providers-table">
            <colgroup>
              <col className="w-[38%] sm:w-1/3" />
              <col />
            </colgroup>
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 font-semibold text-slate-700 border-b border-slate-200 align-top">
                  Provider
                </th>
                <th className="px-4 py-3 font-semibold text-slate-700 border-b border-slate-200 align-top">
                  What they do for PeopleiKnow
                </th>
              </tr>
            </thead>
            <tbody>
              {PROVIDERS.map((p) => (
                <tr key={p.name} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-800 align-top">
                    {p.name}
                  </td>
                  <td className="px-4 py-3 text-slate-600 align-top">{p.does}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LegalSection>

      <LegalSection title="Where your information is stored">
        <p>
          <Brand /> is based in Canada, but your information is stored and
          processed in the United States. Some of our service providers may
          also process information in other countries outside Canada. While
          your information is in the United States or another country, it may
          be accessible to courts, law enforcement and national security
          authorities there under the laws of that country, including US law.
          To ask about how our service providers outside Canada handle
          personal information, email{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="Data retention">
        <p>
          We keep your information for as long as your account is active, or
          as needed to provide <Brand />.
        </p>
        <p>
          <span className="font-medium text-slate-700">Deleting your account.</span>{" "}
          You can delete your account anytime in the app under Settings &gt;
          Your data &amp; account &gt; Delete my account. When you do:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            Your profile, your contacts, groups, notes, photos, notifications,
            sign-in identities and other data you own are permanently
            deleted.
          </li>
          <li>In the iPhone app, Sign in with Apple is revoked (see above).</li>
          <li>
            People who had saved you keep the card they made for you, because
            it's their information. Their card stops receiving updates from
            you, and they're told you're no longer on <Brand />.
          </li>
          <li>
            We keep a minimal record that the deletion happened (including
            your email address and <Brand /> ID), to honour the deletion and
            for security and legal purposes.
          </li>
        </ul>
        <p>
          Deleted information may remain in our backups for a period after
          deletion, until those backups are deleted in the normal course. We
          do not use backups for any other purpose.
        </p>
        <p>
          You can also email{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>{" "}
          to ask us to delete your account.
        </p>
        <p>
          <span className="font-medium text-slate-700">Deleted contacts.</span>{" "}
          Contacts you delete are removed from your active address book and
          kept in a deleted-items record so the deletion syncs across your
          devices.
        </p>
        <p>
          We may keep some information longer where the law requires it, or
          for legitimate purposes like resolving disputes and preventing
          abuse.
        </p>
        <p>
          For deleted contacts, the deletion record, crash reports and sign-in
          sessions, we keep information only as long as needed for the
          purposes described in this policy, then delete or anonymize it.
        </p>
      </LegalSection>

      <LegalSection title="Data security">
        <p>
          We use modern security practices to protect your information. These
          include encryption in transit, hashed passwords (never stored in
          plain text), optional two-step verification, rate limits to prevent
          scraping, and keeping each account's data separate. No method of
          sending or storing data is perfectly secure, but we work hard to
          protect your data and keep improving as <Brand /> grows. See our{" "}
          <a className="text-blue-700 font-medium hover:underline" href="/security">
            Security page
          </a>{" "}
          for more.
        </p>
      </LegalSection>

      <LegalSection title="Shared devices">
        <p>
          For privacy, don't share your <Brand /> account or leave it signed
          in on a device others use. If more than one person uses the same
          device, each person should use their own <Brand /> account and sign
          out when finished. <Brand /> keeps locally stored and synced
          contact data separated by signed-in account. If you think someone
          else has accessed your account or personal information, change your
          password or sign-in method, sign out of other sessions in the app,
          and contact us promptly.
        </p>
      </LegalSection>

      <LegalSection title="Your choices and rights">
        <LegalSubheading>What you can do in the app, anytime</LegalSubheading>
        <ul className="list-disc pl-5 space-y-2">
          <li>See and update your account and My Card.</li>
          <li>
            Choose what My Card shows, what your connections get, and what
            selected contacts get.
          </li>
          <li>
            Export a copy of your data (JSON, or CSV for contacts) under
            Settings &gt; Your data &amp; account.
          </li>
          <li>Delete contacts, or your whole account.</li>
          <li>
            Turn device permissions (contacts, location, camera, photos,
            microphone, notifications) on or off in your iPhone Settings.
          </li>
          <li>
            Disconnect Google or Apple sign-in from your Google or Apple
            account settings.
          </li>
        </ul>

        <LegalSubheading>Your rights in Canada</LegalSubheading>
        <p>
          <Brand /> is based in Alberta. We handle personal information in
          line with Canada's Personal Information Protection and Electronic
          Documents Act (PIPEDA) and Alberta's Personal Information Protection
          Act (PIPA), whichever applies. Under these laws, you can:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            Ask for access to the personal information we hold about you, and
            how it's been used and disclosed.
          </li>
          <li>Ask us to correct information that's wrong or incomplete.</li>
          <li>
            Withdraw your consent, subject to legal or contractual limits. For
            some features, this may mean we can't keep providing them.
          </li>
          <li>
            Ask questions about our privacy practices, including our use of
            service providers outside Canada.
          </li>
        </ul>
        <p>
          If you're not satisfied with how we handle a concern, you can
          complain to the{" "}
          <a
            className="text-blue-700 font-medium hover:underline"
            href="https://priv.gc.ca"
            target="_blank"
            rel="noopener noreferrer"
          >
            Office of the Privacy Commissioner of Canada
          </a>{" "}
          or the{" "}
          <a
            className="text-blue-700 font-medium hover:underline"
            href="https://oipc.ab.ca"
            target="_blank"
            rel="noopener noreferrer"
          >
            Office of the Information and Privacy Commissioner of Alberta
          </a>
          .
        </p>
        <p>
          Our privacy contact is Alex Danzinger, Managing Principal (Privacy
          Officer), at{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>
          .
        </p>

        <LegalSubheading>Rights elsewhere</LegalSubheading>
        <p>
          Depending on where you live, for example in the European Union, the
          United Kingdom or California, you may have additional rights. These
          can include the right to access, correct, delete or move (port)
          your personal information, to object to or restrict some
          processing, and to know what categories of information we collect
          and why. We don't sell or "share" personal information for targeted
          advertising as those terms are used in California law, and we won't
          treat you differently for using your rights. Where the law requires
          a legal basis for using your information, we rely on providing the
          service you asked for, your consent (for example, for device
          permissions), our legitimate interests in running and securing{" "}
          <Brand />, and legal obligations.
        </p>
        <p>
          To make any request, email{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>
          . We may need to confirm your identity first, and we'll respond
          within the time required by law.
        </p>
      </LegalSection>

      <LegalSection title="Children">
        <p>
          <Brand /> is not directed to children under 13, and we don't
          knowingly collect personal information from children under 13 as
          account holders. If you're a parent or guardian and think your
          child under 13 has given us personal information, contact us at{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>{" "}
          and we'll delete it.
        </p>
        <p>
          <Brand /> members may store contacts of any age, for example family
          members, in their own private contact book. That information
          belongs to the member's private address book and is handled as
          described in "Other people's information".
        </p>
      </LegalSection>

      <LegalSection title="Changes to this policy">
        <p>
          <Brand /> is still growing, and our features and data practices will
          change as the product matures. When we update this policy, we'll
          change the "Last updated" date at the top. If we make a significant
          change to how we handle your information, we'll let you know in the
          app or by email before it takes effect, and we'll ask for your
          consent where the law requires it.
        </p>
      </LegalSection>

      <LegalSection title="Contact us">
        <p>
          Questions about privacy or your data? Email{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>
          . We read every message.
        </p>
        <p>
          <Brand /> is operated by Foosball Consulting, Inc., Calgary,
          Alberta, Canada.
        </p>
        <p>
          Privacy contact: Alex Danzinger, Managing Principal (Privacy
          Officer),{" "}
          <a className="text-blue-700 font-medium hover:underline" href={`mailto:${BETA_EMAIL}`}>
            {BETA_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
